/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { NetworkStatus, gql, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconFilter from '@apollo/icons/default/IconFilter.svg';
import IconSortAscending from '@apollo/icons/small/IconSortAscending.svg';
import IconSortDescending from '@apollo/icons/small/IconSortDescending.svg';
import {
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuHeader,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuTitle,
  Tooltip,
} from '@apollo/orbit';
import { jsx } from '@emotion/react';
import React, { useCallback, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useHistory, useLocation } from 'react-router-dom';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { ClientFilterPopover } from 'src/components/clientFilter/ClientFilterPopover';
import { filterTypes } from 'src/components/common/filterProvider/FilterProvider';
import { MultiSelect } from 'src/components/multiSelect/MultiSelect';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { CustomTimeRange, date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  OperationInsightsListOrderByColumn,
  OperationType,
  Ordering,
} from 'src/lib/graphqlTypes/types';
import {
  NewNormalizedMetricsFilterField,
  NormalizedMetricsField,
  NormalizedMetricsFilterField,
  isValidOperationsMetricsField,
  isValidOperationsMetricsFilterFields,
  metricConfig,
} from 'src/lib/metrics';
import { AnyQueryParams, mergeQueryParams } from 'src/lib/routing';

import { GroupedValues } from './FieldsList';
import { OperationsListBody } from './insightsListBody/OperationsListBody';
import { OperationsListItemPlaceholder } from './insightsListItemPlaceholder/OperationsListItemPlaceholder';
import { InsightsNoSearchResults } from './insightsNoSearchResults/InsightsNoSearchResults';
import { SelectedOperationsListItem } from './insightsSelectedListItem/SelectedOperationsListItem';
import { PaginationControls } from './paginationControls/PaginationControls';
import { SearchFilter } from './searchFilter/SearchFilter';
import { TotalCountBadge } from './totalCountBadge/TotalCountBadge';

export type SelectedOperation = {
  id: string;
  type?: OperationType;
  name?: string;
};

const operationsListQuery = gql`
  query UI__OperationsListQuery(
    $graphId: ID!
    $name: String!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $from: Timestamp!
    $to: Timestamp!
    $filter: OperationInsightsListFilterInput
    $orderBy: OperationInsightsListOrderByInput
  ) {
    graph(id: $graphId) {
      id
      variant(name: $name) {
        id
        operationInsightsList(
          first: $first
          after: $after
          last: $last
          before: $before
          from: $from
          to: $to
          filter: $filter
          orderBy: $orderBy
        ) {
          operations: nodes {
            id
            name
            displayName
            type
            requestCount
            requestCountPerMin
            errorCount
            errorCountPerMin
            errorPercentage
            cacheHitRate
            cacheTtlP50Ms
            serviceTimeP50Ms
            serviceTimeP90Ms
            serviceTimeP95Ms
            serviceTimeP99Ms
            totalDurationMs
            signatureBytes
          }
          pageInfo {
            startCursor
            endCursor
          }
          totalCount
        }
      }
    }
  }
`;

const getOperationSortColumn = (
  operationsSort: string | undefined,
): OperationInsightsListOrderByColumn => {
  const colMap: {
    [key in NormalizedMetricsField]: OperationInsightsListOrderByColumn;
  } = {
    alphabetically: OperationInsightsListOrderByColumn.OPERATION_NAME,
    requestsCount: OperationInsightsListOrderByColumn.REQUEST_COUNT,
    requestRate: OperationInsightsListOrderByColumn.REQUEST_COUNT_PER_MIN,
    errorsCount: OperationInsightsListOrderByColumn.ERROR_COUNT,
    errorRate: OperationInsightsListOrderByColumn.ERROR_COUNT_PER_MIN,
    errorPercentage: OperationInsightsListOrderByColumn.ERROR_PERCENTAGE,
    cacheHitRate: OperationInsightsListOrderByColumn.CACHE_HIT_RATE,
    cacheTTLP50: OperationInsightsListOrderByColumn.CACHE_TTL_P50,
    serviceTimeP50: OperationInsightsListOrderByColumn.SERVICE_TIME_P50,
    serviceTimeP90: OperationInsightsListOrderByColumn.SERVICE_TIME_P90,
    serviceTimeP95: OperationInsightsListOrderByColumn.SERVICE_TIME_P95,
    serviceTimeP99: OperationInsightsListOrderByColumn.SERVICE_TIME_P99,
    totalDurationMs: OperationInsightsListOrderByColumn.TOTAL_DURATION_MS,
    querySize: OperationInsightsListOrderByColumn.SIGNATURE_BYTES,
  };

  return operationsSort && operationsSort in colMap
    ? colMap[operationsSort as NormalizedMetricsField]
    : OperationInsightsListOrderByColumn.REQUEST_COUNT;
};

const getOperationType = (
  operationsFilter: string | undefined,
): GraphQLTypes.OperationType | undefined => {
  switch (operationsFilter) {
    case 'subscriptions':
      return OperationType.SUBSCRIPTION;
    case 'mutations':
      return OperationType.MUTATION;
    case 'queries':
      return OperationType.QUERY;
    default:
      return undefined;
  }
};

interface OperationsListProps {
  graphId: string;
  graphVariant: string;
  range: TimeRangeOption;
  bounds: CustomTimeRange;
  selectedOperation?: SelectedOperation;
  filter: filterTypes;
  hasResetOperationsFilter: boolean;
  setHasResetOperationsFilter: (value: boolean) => void;
  clientFilterCount?: number;
}

const filterOrderMap = new Map<NewNormalizedMetricsFilterField, number>([
  ['queries', 0],
  ['mutations', 1],
  ['subscriptions', 2],
  ['unnamed', 3],
  ['unregistered', 4],
]);

const ORFilterOptions: NewNormalizedMetricsFilterField[] = [
  'mutations',
  'queries',
  'subscriptions',
];

const ANDFilterOptions: NewNormalizedMetricsFilterField[] = [
  'unnamed',
  'unregistered',
];

const groupedValues: GroupedValues = [
  {
    values: ORFilterOptions,
    opposing: false,
    dividerAfter: 'subscriptions',
  },
  {
    values: ANDFilterOptions,
    opposing: false,
  },
];

const stableEmptyArray: [] = [];
export const OperationsList = (props: OperationsListProps) => {
  const {
    graphId,
    graphVariant,
    range,
    bounds,
    selectedOperation,
    filter,
    hasResetOperationsFilter,
    setHasResetOperationsFilter,
    clientFilterCount,
  } = props;
  const {
    operationsDir,
    operationSearch,
    operationsAfter,
    operationsBefore,
    operationsPage = 0,
    operationsSort = 'requestsCount',
    operationsFilters: operationsFiltersTemp = stableEmptyArray,
    operationsPageDir = 'first',
    fastModeRange,
  } = useRouteParams(insightsRouteConfig);
  const sortDirectionIsDescending =
    operationsDir === 'desc' || operationsDir === undefined;

  const location = useLocation();
  const history = useHistory();
  const isInBackground = useIsAppInBackgroundContext();
  const [inViewRef, inView] = useInView();
  const trackCustomEvent = useTrackCustomEvent();
  const [operationSearchInputValue, setOperationSearchInputValue] = useState(
    operationSearch ?? '',
  );

  React.useEffect(() => {
    // We manage the non-debounced search state in this component but the URL
    // owns the debounced search state. If components reset the URL state we
    // need to sync those changes here.
    if (!operationSearch) {
      setOperationSearchInputValue('');
    }
  }, [operationSearch]);

  let operationsFilters = operationsFiltersTemp;
  if (!hasResetOperationsFilter && operationsFilters.length === 0) {
    operationsFilters = stableEmptyArray; // All operations
  }

  const resetOperationSearchInputValue = () => {
    setOperationSearchInputValue('');
  };

  const { timeFrom, timeTo, pollInterval } = date.getDateBounds(
    range,
    bounds,
    undefined,
    fastModeRange,
  );

  const statsSortKey = isValidOperationsMetricsField(operationsSort)
    ? operationsSort
    : 'requestsCount';

  const statsFilterKeys = isValidOperationsMetricsFilterFields(
    operationsFilters,
  )
    ? operationsFilters
    : stableEmptyArray;

  const setQueryParams = (newParams: AnyQueryParams) => {
    history.push({
      ...location,
      search: mergeQueryParams(location.search, newParams),
    });
  };

  const updateFilterOptions = (
    selectedFilterOptions: NormalizedMetricsFilterField[],
  ) => {
    if (selectedFilterOptions.length === 0) {
      setHasResetOperationsFilter(true);
    }

    setQueryParams({
      operationsFilters: selectedFilterOptions,
      operationsPage: 0,
      operationsAfter: undefined,
      operationsBefore: undefined,
    });
  };

  const updateSortOption = (newOperationsSort: NormalizedMetricsField) => {
    const asc = sortDirectionLongformToReadableMap[Ordering.ASCENDING];
    const desc = sortDirectionLongformToReadableMap[Ordering.DESCENDING];
    const prevOperationsSort = operationsSort;
    // When sorting Alphabetically, sort asc by default. Sort desc for any other
    // sort metric
    const newOperationsDir =
      newOperationsSort === 'alphabetically'
        ? asc
        : prevOperationsSort === 'alphabetically'
          ? desc
          : operationsDir;

    setQueryParams({
      operationsSort: newOperationsSort,
      operationsDir: newOperationsDir,
      operationsPage: 0,
      operationsAfter: undefined,
      operationsBefore: undefined,
    });

    trackCustomEvent({
      category: 'Insights L3 Operations List',
      action: 'selected_insights_operation_sort',
      label: newOperationsSort,
    });
  };

  const sortDirectionLongformToReadableMap = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
  };

  const updateSortDirection = (direction: Ordering) => {
    setQueryParams({
      operationsDir: sortDirectionLongformToReadableMap[direction],
      operationsPage: 0,
      operationsAfter: undefined,
      operationsBefore: undefined,
    });

    trackCustomEvent({
      category: 'Insights L3 Operations List',
      action: 'selected_operations_sort_direction',
      label: direction,
    });
  };

  const operationListPageSize = 50;
  const paginationParams = operationsAfter
    ? {
        first: operationListPageSize,
        after: operationsAfter,
      }
    : operationsBefore
      ? {
          last: operationListPageSize,
          before: operationsBefore,
        }
      : operationsPageDir === 'last'
        ? {
            last: operationListPageSize,
          }
        : {
            first: operationListPageSize,
          };

  const operationTypesFilter = useMemo(() => {
    const mappedOperationsFilters = operationsFilters
      .map(getOperationType)
      .filter(
        (value) => value !== undefined,
      ) as unknown as GraphQLTypes.OperationType[];

    return mappedOperationsFilters.length === 0
      ? undefined
      : mappedOperationsFilters;
  }, [operationsFilters]);

  const skip = !graphId || !graphVariant || isInBackground || !inView;

  const {
    data: newData,
    previousData,
    stopPolling,
    networkStatus,
  } = useQuery<
    GraphQLTypes.UI__OperationsListQuery,
    GraphQLTypes.UI__OperationsListQueryVariables
  >(operationsListQuery, {
    variables: {
      ...paginationParams,
      graphId,
      name: graphVariant || '',
      from: timeFrom,
      to: timeTo,
      filter: {
        ...filter,
        search: operationSearch,
        operationTypes: operationTypesFilter,
        isUnnamed: statsFilterKeys.includes('unnamed') ? true : undefined,
        isUnregistered: statsFilterKeys.includes('unregistered')
          ? true
          : undefined,
      },
      orderBy: {
        column: getOperationSortColumn(operationsSort),
        direction:
          operationsDir === 'asc' ? Ordering.ASCENDING : Ordering.DESCENDING,
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    pollInterval,
    skip,
    skipPollAttempt: useCallback(() => skip, [skip]),
  });

  const data = newData ?? previousData;
  const totalCount = data?.graph?.variant?.operationInsightsList.totalCount;
  const operationInsightsList = data?.graph?.variant?.operationInsightsList;

  // We see a "setVariables" state when paginating, searching or changing filters since
  // we use the URL to hold the pagination state.  Including the others for
  // completeness.
  const activeRefetching = [
    NetworkStatus.setVariables,
    NetworkStatus.refetch,
    NetworkStatus.fetchMore,
  ].includes(networkStatus);

  // We see a "loading" state when returning from the background and
  // "poll" when polling.
  const passiveRefetching = [
    NetworkStatus.loading,
    NetworkStatus.poll,
  ].includes(networkStatus);

  const isSelectedOperationInCurrentPage =
    !!selectedOperation &&
    !!operationInsightsList?.operations?.find(
      (operation) => operation.id === selectedOperation?.id,
    );

  const numPages =
    (totalCount && Math.ceil(totalCount / operationListPageSize) - 1) ?? 1;

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  const canFilterOnClients = !!currentPlan.currentPlan?.clients;
  const canViewClientVersions =
    currentPlan.currentPlan?.clientVersions ?? false;

  return (
    <div className="flex h-full flex-col" ref={inViewRef}>
      <div className="flex items-center">
        <SearchFilter
          placeholderText="Search for an operation"
          inputValue={operationSearchInputValue}
          onInputChange={setOperationSearchInputValue}
          onSearch={(search: string) => {
            history.replace({
              ...location,
              search: mergeQueryParams(location.search, {
                operationSearch: search,
                operationsPage: 0,
                operationsAfter: undefined,
                operationsBefore: undefined,
              }),
            });

            trackCustomEvent({
              category: 'Insights L3 Operations List',
              action: 'executed_operations_search',
            });
          }}
          onClear={() => {
            trackCustomEvent({
              category: 'Insights L3 Operations List',
              action: 'cleared_operations_search',
            });
          }}
        />
        {canFilterOnClients && (
          <ClientFilterPopover
            graphId={graphId}
            canViewVersions={canViewClientVersions}
            clientFilterCount={clientFilterCount}
          />
        )}
      </div>
      <div className="mb-2 mt-3 flex items-center justify-between font-semibold text-link">
        <MultiSelect
          className="min-w-64 !p-2"
          valueTitle="Filter Operations"
          valueTitleClassName="text-sm"
          clearAllClassName="font-normal"
          clearAllText="Clear filters"
          canSelectExclusive
          allValues={
            Object.keys(
              metricConfig.newAllOperationsFilters,
            ) as NewNormalizedMetricsFilterField[]
          }
          groupedValues={groupedValues}
          orderValuesBy={(value) =>
            filterOrderMap.get(value) ?? assertUnreachable(value as never)
          }
          selectedValues={new Set(statsFilterKeys)}
          setSelectedValues={(selectedValues) =>
            updateFilterOptions(Array.from(selectedValues.values()))
          }
          labelEnd={(value) =>
            operationsFilters.length > 1
              ? ORFilterOptions.includes(value) &&
                (operationTypesFilter ?? []).length > 1
                ? 'OR'
                : ANDFilterOptions.includes(value)
                  ? 'AND'
                  : undefined
              : undefined
          }
          triggerNode={
            <Button
              size="sm"
              variant="hidden"
              leftIcon={<IconFilter />}
              rightIcon={<IconChevronDown />}
              data-testid="filter-operations-button"
            >
              {statsFilterKeys.length === 0
                ? 'View all'
                : statsFilterKeys.length === 1 &&
                    statsFilterKeys[0] !== undefined
                  ? metricConfig.newAllOperationsFilters[statsFilterKeys[0]]
                  : `${statsFilterKeys.length} filters`}
            </Button>
          }
          labelForValue={(value) => metricConfig.newAllOperationsFilters[value]}
        />
        <div className="flex items-center">
          <Menu matchWidth>
            <MenuButton
              as={Button}
              size="sm"
              variant="hidden"
              rightIcon={<IconChevronDown className="ml-1 text-icon-primary" />}
            >
              <span className="truncate">
                {metricConfig.allOperationsMetrics[statsSortKey]}
              </span>
            </MenuButton>
            <MenuContent>
              <MenuHeader>
                <MenuTitle>Sort operations by</MenuTitle>
              </MenuHeader>
              <MenuList>
                <MenuOptionGroup
                  type="radio"
                  value={
                    operationsSort as Parameters<typeof updateSortOption>[0]
                  }
                  onChange={(key: NormalizedMetricsField) => {
                    updateSortOption(key);
                  }}
                >
                  {Object.entries(metricConfig.allOperationsMetrics).map(
                    ([key, value]) => (
                      <MenuItemOption
                        key={key}
                        value={key}
                        type="radio"
                        className="group"
                        description={
                          key === 'alphabetically'
                            ? 'Total requests will be shown when selected.'
                            : undefined
                        }
                      >
                        {value}
                      </MenuItemOption>
                    ),
                  )}
                </MenuOptionGroup>
              </MenuList>
            </MenuContent>
          </Menu>
        </div>
      </div>
      {((networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.setVariables) &&
        previousData?.graph?.variant?.operationInsightsList.totalCount === 0) ||
      !data ||
      !data?.graph?.variant ||
      !inView ? (
        <OperationsListItemPlaceholder />
      ) : operationInsightsList?.totalCount === 0 ? (
        <div>
          {selectedOperation !== undefined &&
          !isSelectedOperationInCurrentPage ? (
            <SelectedOperationsListItem
              loading={activeRefetching}
              {...selectedOperation}
            />
          ) : null}
          <InsightsNoSearchResults
            entity="Operation"
            resetSearch={resetOperationSearchInputValue}
            graphId={graphId}
            graphVariant={graphVariant}
          />
        </div>
      ) : (
        <>
          <div className="hide-scrollbar flex-1 overflow-y-auto overflow-x-hidden border-t-[1px] border-primary">
            <>
              {selectedOperation !== undefined &&
                !isSelectedOperationInCurrentPage && (
                  <SelectedOperationsListItem
                    loading={activeRefetching}
                    {...selectedOperation}
                  />
                )}
              <div className="ml-4 mt-4 flex items-center">
                <Heading variant="title" className="mr-1">
                  {operationsFilters.length > 0 ? 'Results' : 'All operations'}
                </Heading>
                <TotalCountBadge count={totalCount} />
                <Tooltip
                  label={`Switch to ${
                    sortDirectionIsDescending ? 'Ascending' : 'Descending'
                  }`}
                >
                  <IconButton
                    size="xs"
                    className="ml-auto mr-2"
                    variant="hidden"
                    aria-label={`Switch to ${
                      sortDirectionIsDescending ? 'Ascending' : 'Descending'
                    }`}
                    onClick={() => {
                      updateSortDirection(
                        sortDirectionIsDescending
                          ? Ordering.ASCENDING
                          : Ordering.DESCENDING,
                      );
                    }}
                  >
                    {sortDirectionIsDescending ? (
                      <IconSortDescending />
                    ) : (
                      <IconSortAscending />
                    )}
                  </IconButton>
                </Tooltip>
              </div>
            </>
            <OperationsListBody
              serviceId={graphId}
              data={data}
              selectedQueryId={selectedOperation?.id}
              selectedQueryName={selectedOperation?.name}
              statsSortKey={statsSortKey}
              loading={activeRefetching}
            />
          </div>

          {totalCount ? (
            <PaginationControls
              currentPage={operationsPage}
              totalCount={totalCount}
              pageSize={operationListPageSize}
              numPages={numPages}
              loading={passiveRefetching}
              disabled={activeRefetching || passiveRefetching}
              onFirst={(nextPage) => {
                history.replace({
                  ...location,
                  search: mergeQueryParams(location.search, {
                    operationsAfter: undefined,
                    operationsBefore: undefined,
                    operationsPage: nextPage,
                    operationsPageDir: 'first',
                  }),
                });

                trackCustomEvent({
                  category: 'Insights L3 Operations List',
                  action: 'selected_first_page',
                });
              }}
              onPrevious={(nextPage) => {
                // If we're already on the first page then get the first page of
                // results w/out the cursor. This allows the user to get to
                // results that may have drifted due paginating over data which
                // is ingested in real-time.
                if (nextPage === 0) {
                  history.replace({
                    ...location,
                    search: mergeQueryParams(location.search, {
                      operationsAfter: undefined,
                      operationsBefore: undefined,
                      operationsPage: nextPage,
                      operationsPageDir: 'first',
                    }),
                  });
                } else {
                  history.replace({
                    ...location,
                    search: mergeQueryParams(location.search, {
                      operationsAfter: undefined,
                      operationsBefore:
                        operationInsightsList?.pageInfo.startCursor ??
                        undefined,
                      operationsPage: nextPage,
                      operationsPageDir: 'last',
                    }),
                  });
                }

                trackCustomEvent({
                  category: 'Insights L3 Operations List',
                  action: 'selected_prev_page',
                });
              }}
              onNext={(nextPage) => {
                stopPolling();

                // If we're already on the last page then get the last page of
                // results w/out the cursor. This allows the user to get to
                // results that may have drifted due paginating over data which
                // is ingested in real-time.
                if (nextPage === numPages) {
                  history.replace({
                    ...location,
                    search: mergeQueryParams(location.search, {
                      operationsAfter: undefined,
                      operationsBefore: undefined,
                      operationsPage: nextPage,
                      operationsPageDir: 'last',
                    }),
                  });
                } else {
                  history.replace({
                    ...location,
                    search: mergeQueryParams(location.search, {
                      operationsBefore: undefined,
                      operationsAfter:
                        operationInsightsList?.pageInfo.endCursor ?? undefined,
                      operationsPage: nextPage,
                      operationsPageDir: 'first',
                    }),
                  });
                }

                trackCustomEvent({
                  category: 'Insights L3 Operations List',
                  action: 'selected_next_page',
                });
              }}
              onLast={(nextPage) => {
                history.replace({
                  ...location,
                  search: mergeQueryParams(location.search, {
                    operationsAfter: undefined,
                    operationsBefore: undefined,
                    operationsPage: nextPage,
                    operationsPageDir: 'last',
                  }),
                });

                trackCustomEvent({
                  category: 'Insights L3 Operations List',
                  action: 'selected_last_page',
                });
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
};
